/**
 * Harness Layer: Content service
 * Pure functions for filtering, sorting, grouping, and search.
 * No React state, no side effects. All functions are unit-testable.
 *
 * Interface boundary:
 *   Input: posts array, filter params object
 *   Output: filtered/sorted/grouped posts array
 */

// T-Cron-2026-10-23 14:00 — 改进清单 #15「搜索:拼音」。
// 拼音首字母表是**构建期离线生成**的字面量(见 scripts/_gen_pinyin.py),
// 运行时零依赖 —— 不引入 pypinyin(~200KB),手写表只有 ~3KB。
import { getBookPinyin, isPinyinQuery } from './pinyin.js';

const ContentService = (() => {

  /**
   * Get all version keys from a post (e.g. ['A', 'B']).
   * @param {Object} post
   * @returns {string[]}
   */
  function getVersionKeys(post) {
    if (post.versions && typeof post.versions === 'object') {
      return Object.keys(post.versions);
    }
    return [];
  }

  /**
   * Check if a post has versioned data (A/B model).
   * @param {Object} post
   * @returns {boolean}
   */
  function hasVersions(post) {
    return post.versions && typeof post.versions === 'object' && Object.keys(post.versions).length > 0;
  }

  /**
   * Filter posts by search query. Matches across all versions: title, body, book, hotTopic, tags, summary.
   *
   * T-Cron-2026-10-23 14:00 — 改进清单 #15「搜索:拼音」。
   * 原实现只做中文字面量匹配,用户在搜索框打 "sjxl" 一篇都搜不到。
   * 现在**附加**一条拼音通道(纯 ASCII 查询才启用):
   *   查询 "sjxl"  → 命中《设计心理学》(pinyin = 'sjxlx',前缀匹配)
   *   查询 "hte"   → 命中《黑天鹅》
   * 中文查询行为完全不变(原分支优先)。
   *
   * @param {Array} posts
   * @param {string} query
   * @returns {Array}
   */
  function searchPosts(posts, query) {
    if (!query || !query.trim()) return posts;
    const q = query.toLowerCase().trim();

    // 拼音通道:只有纯 ASCII(且含字母)的查询才启用,避免干扰中文/数字查询。
    const pinyinMode = isPinyinQuery(q);

    return posts.filter(post => {
      // Shared fields
      if (post.book && post.book.toLowerCase().includes(q)) return true;
      if (post.hotTopic && post.hotTopic.toLowerCase().includes(q)) return true;

      // 拼音通道:书名首字母前缀/子串匹配(前缀优先,保证 "sj" 也能命中《设计心理学》)
      if (pinyinMode) {
        const py = getBookPinyin(post.book);
        if (py && (py.startsWith(q) || py.includes(q))) return true;
      }

      // Versioned fields: search across all versions
      if (hasVersions(post)) {
        return Object.values(post.versions).some(v =>
          (v.title && v.title.toLowerCase().includes(q)) ||
          (v.body && v.body.toLowerCase().includes(q)) ||
          (v.summary && v.summary.toLowerCase().includes(q)) ||
          (v.tags && v.tags.some(t => t.toLowerCase().includes(q)))
        );
      }

      // Legacy flat model
      if (post.title && post.title.toLowerCase().includes(q)) return true;
      if (post.body && post.body.toLowerCase().includes(q)) return true;
      if (post.tags && post.tags.some(t => t.toLowerCase().includes(q))) return true;
      return false;
    });
  }

  /**
   * Filter posts by book name.
   * @param {Array} posts
   * @param {string|null} book - exact match, null/empty means all
   * @returns {Array}
   */
  function filterByBook(posts, book) {
    if (!book) return posts;
    return posts.filter(p => p.book === book);
  }

  /**
   * Filter posts by date.
   * @param {Array} posts
   * @param {string|null} date - YYYY-MM-DD, null/empty means all
   * @returns {Array}
   */
  function filterByDate(posts, date) {
    if (!date) return posts;
    return posts.filter(p => p.date === date);
  }

  /**
   * Filter posts by hotspot / hot topic.
   * @param {Array} posts
   * @param {string|null} hotspot
   * @returns {Array}
   */
  function filterByHotspot(posts, hotspot) {
    if (!hotspot) return posts;
    return posts.filter(p => p.hotTopic === hotspot);
  }

  /**
   * Filter posts by tag. Checks tags across all versions.
   * @param {Array} posts
   * @param {string|null} tag
   * @returns {Array}
   */
  function filterByTag(posts, tag) {
    if (!tag) return posts;
    return posts.filter(p => {
      if (hasVersions(p)) {
        return Object.values(p.versions).some(v => v.tags && v.tags.includes(tag));
      }
      return p.tags && p.tags.includes(tag);
    });
  }

  /**
   * Filter posts by favorite status.
   * @param {Array} posts
   * @param {boolean|null} onlyFavorites - true = only favs, false = only non-favs, null = all
   * @returns {Array}
   */
  function filterByFavorite(posts, onlyFavorites) {
    if (onlyFavorites === null || onlyFavorites === undefined) return posts;
    return posts.filter(p => p.favorite === onlyFavorites);
  }

  /**
   * Filter posts by source (txt / md).
   * @param {Array} posts
   * @param {string|null} source
   * @returns {Array}
   */
  function filterBySource(posts, source) {
    if (!source) return posts;
    return posts.filter(p => p.source === source);
  }

  /**
   * Filter posts by angle. Checks angle across all versions.
   * @param {Array} posts
   * @param {string|null} angle
   * @returns {Array}
   */
  function filterByAngle(posts, angle) {
    if (!angle) return posts;
    return posts.filter(p => {
      if (hasVersions(p)) {
        return Object.values(p.versions).some(v => v.angle === angle);
      }
      return p.angle === angle;
    });
  }

  /**
   * Filter posts by version key ("A" = 热点主导, "B" = 书籍点睛, "C" = 书籍主导).
   * Only keeps posts that contain the given version.
   * @param {Array} posts
   * @param {string|null} version
   * @returns {Array}
   */
  function filterByVersion(posts, version) {
    if (!version) return posts;
    return posts.filter(p => p.versions && p.versions[version]);
  }

  /**
   * Filter posts by category (e.g. 财经/经济).
   * @param {Array} posts
   * @param {string|null} category
   * @returns {Array}
   */
  function filterByCategory(posts, category) {
    if (!category) return posts;
    return posts.filter(p => p.category === category);
  }

  /**
   * Filter posts by batch (早间 / 午间 / 下午 / 晚间).
   * @param {Array} posts
   * @param {string|null} batch
   * @returns {Array}
   */
  function filterByBatch(posts, batch) {
    if (!batch) return posts;
    return posts.filter(p => p.batch === batch);
  }

  /**
   * Get all unique batches from posts, with post count.
   * Canonical order: 早间, 午间, 下午, 晚间 (future batches appended after).
   * @param {Array} posts
   * @returns {Array<{name: string, count: number}>}
   */
  function getBatchList(posts) {
    const CANON = ['早间', '午间', '下午', '晚间'];
    const map = {};
    for (const post of posts) {
      if (post.batch) {
        map[post.batch] = (map[post.batch] || 0) + 1;
      }
    }
    const names = Object.keys(map);
    names.sort((a, b) => {
      const ia = CANON.indexOf(a);
      const ib = CANON.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return 0;
    });
    return names.map(name => ({ name, count: map[name] }));
  }

  /**
   * Apply all filters at once. Main filter entry point.
   * @param {Array} posts
   * @param {Object} filters
   * @param {string} [filters.search]
   * @param {string} [filters.book]
   * @param {string} [filters.date]
   * @param {string} [filters.tag]
   * @param {string} [filters.angle]
   * @param {string} [filters.source]
   * @param {string} [filters.version] - "A" | "B" | "C"
   * @param {string} [filters.category]
   * @param {string} [filters.batch] - 早间 | 午间 | 下午 | 晚间
   * @param {boolean} [filters.onlyFavorites]
   * @returns {Array}
   */
  function applyFilters(posts, filters) {
    let result = posts;
    if (filters.search) result = searchPosts(result, filters.search);
    if (filters.book) result = filterByBook(result, filters.book);
    if (filters.date) result = filterByDate(result, filters.date);
    if (filters.hotspot) result = filterByHotspot(result, filters.hotspot);
    if (filters.tag) result = filterByTag(result, filters.tag);
    if (filters.angle) result = filterByAngle(result, filters.angle);
    if (filters.source) result = filterBySource(result, filters.source);
    if (filters.version) result = filterByVersion(result, filters.version);
    if (filters.category) result = filterByCategory(result, filters.category);
    if (filters.batch) result = filterByBatch(result, filters.batch);
    if (filters.onlyFavorites) result = filterByFavorite(result, true);
    return result;
  }

  /**
   * Sort posts by generation time descending (newest first).
   * Priority: genTime (modified_time) > date+seq from filename > fallback to end.
   * A/B versions are in the same post object, so they stay adjacent by design.
   * @param {Array} posts
   * @returns {Array}
   */
  function sortByDate(posts) {
    return [...posts].sort((a, b) => {
      // Priority 1: genTime (from file modified_time) — most accurate
      if (a.genTime && b.genTime) {
        return b.genTime - a.genTime;
      }
      if (a.genTime) return -1; // a has genTime, rank higher
      if (b.genTime) return 1;  // b has genTime, rank higher

      // Priority 2: date descending, then seq descending (higher seq = later generated)
      if (a.date !== b.date) {
        return a.date < b.date ? 1 : -1;
      }
      // Same date: higher seq number means later generated (descending)
      return b.seq.localeCompare(a.seq);
    });
  }

  /**
   * Group posts by date into sections.
   * Each section: { date, posts: [] }
   * Dates are sorted descending. Within each date, posts sorted by genTime descending.
   * @param {Array} posts
   * @returns {Array<{date: string, posts: Array}>}
   */
  function groupByDate(posts) {
    const map = {};
    for (const post of posts) {
      if (!map[post.date]) map[post.date] = [];
      map[post.date].push(post);
    }
    const dates = Object.keys(map).sort((a, b) => (a < b ? 1 : -1));
    return dates.map(date => ({
      date,
      // Sort within date group by generation time descending (newest first)
      posts: map[date].sort((a, b) => {
        if (a.genTime && b.genTime) return b.genTime - a.genTime;
        if (a.genTime) return -1;
        if (b.genTime) return 1;
        return b.seq.localeCompare(a.seq);
      }),
    }));
  }

  /**
   * Get all unique books from posts, with post count.
   * @param {Array} posts
   * @returns {Array<{name: string, count: number}>}
   */
  function getBookList(posts) {
    const map = {};
    for (const post of posts) {
      map[post.book] = (map[post.book] || 0) + 1;
    }
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }

  /**
   * Get all unique dates from posts, with post count.
   * @param {Array} posts
   * @returns {Array<{date: string, count: number}>}
   */
  function getDateList(posts) {
    const map = {};
    for (const post of posts) {
      map[post.date] = (map[post.date] || 0) + 1;
    }
    return Object.entries(map)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }

  /**
   * Get all unique tags from posts, with post count.
   * Aggregates tags across all versions. Top 20 most frequent.
   * @param {Array} posts
   * @returns {Array<{name: string, count: number}>}
   */
  function getTagList(posts, limit = 20) {
    const map = {};
    for (const post of posts) {
      const seen = new Set();
      if (hasVersions(post)) {
        for (const v of Object.values(post.versions)) {
          if (v.tags) {
          for (const tag of v.tags) {
            if (!seen.has(tag)) {
              seen.add(tag);
              map[tag] = (map[tag] || 0) + 1;
            }
          }
          }
        }
      } else if (post.tags) {
        for (const tag of post.tags) {
          map[tag] = (map[tag] || 0) + 1;
        }
      }
    }
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  }

  /**
   * Get all unique angles from posts, with post count.
   * Aggregates angles across all versions.
   * @param {Array} posts
   * @returns {Array<{name: string, count: number}>}
   */
  function getAngleList(posts) {
    const map = {};
    for (const post of posts) {
      const seen = new Set();
      if (hasVersions(post)) {
        for (const v of Object.values(post.versions)) {
          if (v.angle && !seen.has(v.angle)) {
            seen.add(v.angle);
            map[v.angle] = (map[v.angle] || 0) + 1;
          }
        }
      } else if (post.angle) {
        map[post.angle] = (map[post.angle] || 0) + 1;
      }
    }
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }

  /**
   * Get all unique sources from posts, with post count.
   * @param {Array} posts
   * @returns {Array<{name: string, count: number}>}
   */
  function getSourceList(posts) {
    const map = {};
    for (const post of posts) {
      if (!post.source) continue;
      map[post.source] = (map[post.source] || 0) + 1;
    }
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }

  /**
   * Get all unique hot topics from posts, with post count.
   * @param {Array} posts
   * @returns {Array<{name: string, count: number}>}
   */
  function getHotspotList(posts) {
    const map = {};
    for (const post of posts) {
      if (!post.hotTopic) continue;
      map[post.hotTopic] = (map[post.hotTopic] || 0) + 1;
    }
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }

  /**
   * Count posts with cover image.
   * @param {Array} posts
   * @returns {number}
   */
  function countWithCover(posts) {
    return posts.filter(p => p.coverUrl).length;
  }

  /**
   * Get date range string from posts.
   * @param {Array} posts
   * @returns {string} e.g. "08.25 – 08.31"
   */
  function getDateRange(posts) {
    if (!posts.length) return '';
    const dates = posts.map(p => p.date).sort();
    const first = dates[0];
    const last = dates[dates.length - 1];
    if (first === last) {
      return `${first.slice(5).replace('-', '.')}`;
    }
    return `${first.slice(5).replace('-', '.')} – ${last.slice(5).replace('-', '.')}`;
  }

  /**
   * Toggle favorite status of a post by id.
   * Returns a new array, does not mutate.
   * @param {Array} posts
   * @param {string} postId
   * @returns {Array}
   */
  function toggleFavorite(posts, postId) {
    return posts.map(post =>
      post.id === postId ? { ...post, favorite: !post.favorite } : post
    );
  }

  /**
   * Count favorited posts.
   * @param {Array} posts
   * @returns {number}
   */
  function countFavorites(posts) {
    return posts.filter(p => p.favorite).length;
  }

  /**
   * Find a post by id.
   * @param {Array} posts
   * @param {string} id
   * @returns {Object|undefined}
   */
  function findById(posts, id) {
    return posts.find(p => p.id === id);
  }

  /**
   * Format date for display.
   * @param {string} dateStr - YYYY-MM-DD
   * @returns {string} e.g. "8月30日 周日"
   */
  function formatDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    const weekday = weekdays[date.getDay()];
    return `${month}月${day}日 ${weekday}`;
  }

  /**
   * Check if a date is "today" (newest date in the dataset).
   * Reference date: the most recent date in the data snapshot.
   * @param {string} dateStr - YYYY-MM-DD
   * @param {Array} posts - optional post array to compute "today" from
   * @returns {boolean}
   */
  function isToday(dateStr, posts) {
    if (!posts || !posts.length) return false;
    const newest = posts.reduce((acc, p) => p.date > acc ? p.date : acc, posts[0].date);
    return dateStr === newest;
  }

  /**
   * Get all unique categories from posts, with post count.
   * @param {Array} posts
   * @returns {Array<{name: string, count: number}>}
   */
  function getCategoryList(posts) {
    const map = {};
    for (const post of posts) {
      if (post.category) {
        map[post.category] = (map[post.category] || 0) + 1;
      }
    }
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }

  // Public API
  return {
    hasVersions,
    getVersionKeys,
    searchPosts,
    filterByBook,
    filterByDate,
    filterByTag,
    filterByAngle,
    filterByFavorite,
    filterByVersion,
    filterByCategory,
    filterByBatch,
    getBatchList,
    applyFilters,
    sortByDate,
    groupByDate,
    getBookList,
    getDateList,
    getTagList,
    getAngleList,
    getSourceList,
    getHotspotList,
    getCategoryList,
    countWithCover,
    getDateRange,
    toggleFavorite,
    countFavorites,
    findById,
    formatDate,
    isToday,
  };
})();

window.ContentService = ContentService;

export default ContentService;
